import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import dayjs, { Dayjs, isDayjs } from 'dayjs'
import { match, P } from 'ts-pattern'
import isPlainObject from 'lodash/isPlainObject'
import { Assert } from '../utils'
import type { Serializable } from '../models/Serializable'
import emptyValueLike from './emptyValueLike'
import isEmptyValue from './isEmptyValue'
import { type FilterQuery, type IQueryableFilter } from './IQueryableFilter'

/**
 * Filter fields, their URL round-trip and the applied/original snapshots,
 * without pagination — for filters that feed something other than a
 * datatable (a dashboard, a report). Datatable filters extend BaseFilter,
 * which adds pagination on top of this.
 */
export abstract class AbstractFilter implements IQueryableFilter {
  private originalData: Record<string, unknown> = {}
  private appliedData: Record<string, unknown> = {}

  /** Populated on the subclass's own prototype by the @Numeric() decorator. */
  private declare numericFields?: Set<string>

  /** Populated on the subclass's own prototype by the @IgnoreFromCount() decorator. */
  private declare uncountedFields?: Set<string>

  hasChanged(): boolean {
    return !isEqual(this.originalData, this.getFilteredData())
  }

  /**
   * Marks the current values as the applied filter state — the snapshot
   * reflected by getFilledFilterCount(). Call on filter submit, not on edit,
   * so badges don't react to in-progress changes.
   */
  applyFilter(): void {
    this.appliedData = this.getFilteredData()
  }

  getFilledFilterCount(): number {
    return Object.entries(this.appliedData).filter(
      ([key, value]: [string, unknown]) => !this.uncountedFields?.has(key) && !isEmptyValue(value),
    ).length
  }

  captureData(): void {
    this.originalData = this.getFilteredData()
  }

  /**
   * A clone with every field reset to its type's empty value, regardless of
   * what this filter's own default/initial values are. Used as the canvas
   * applyQueryParams builds on when restoring from the URL, so a field absent
   * from the URL always means "empty" rather than silently falling back to a
   * non-empty default that can then never be cleared.
   */
  toBlank(): this {
    const blank = cloneDeep(this)

    for (const key of this.fieldKeys()) {
      ;(blank as Record<string, unknown>)[key] = emptyValueLike(
        (this as Record<string, unknown>)[key],
      )
    }

    return blank
  }

  toQueryParams(): Record<string, string | string[]> {
    const params: Record<string, string | string[]> = {}

    for (const key of this.fieldKeys()) {
      const value = (this as Record<string, unknown>)[key]

      if (isEmptyValue(value)) {
        continue
      }

      if (Array.isArray(value)) {
        params[`${key}[]`] = value.map((item: unknown) => this.encodeQueryValue(item))
      } else {
        params[key] = this.encodeQueryValue(value)
      }
    }

    return params
  }

  private encodeQueryValue(value: unknown): string {
    return match(value)
      .when(Assert.isSerializable, (serializable: Serializable) => serializable.serialize())
      .when(isDayjs, (date: Dayjs) => date.toLocalDateTimeString())
      .when(isPlainObject, (obj: object) => JSON.stringify(obj))
      .with(P.string, (text: string) => text)
      .with(P.union(P.number, P.boolean), (primitive: number | boolean) => String(primitive))
      .otherwise((unsupported) => Assert.unreachable(unsupported as never))
  }

  queryParamKeys(): string[] {
    return this.fieldKeys().map((key: string) =>
      Array.isArray((this as Record<string, unknown>)[key]) ? `${key}[]` : key,
    )
  }

  applyQueryParams(query: FilterQuery): void {
    for (const key of this.fieldKeys()) {
      const current = (this as Record<string, unknown>)[key]

      if (Array.isArray(current)) {
        const tokens = this.readArrayParam(query[`${key}[]`])

        if (tokens !== null) {
          ;(this as Record<string, unknown>)[key] = tokens.map((token: string) =>
            this.decodeArrayToken(token),
          )
        }

        continue
      }

      const raw = this.readParam(query[key])

      if (raw !== null) {
        ;(this as Record<string, unknown>)[key] = this.decodeQueryValue(key, current, raw)
      }
    }
  }

  private decodeQueryValue(key: string, template: unknown, raw: string): unknown {
    return match(template)
      .when(Assert.isSerializable, (serializable: Serializable) => serializable.deserialize(raw))
      .when(isDayjs, () => dayjs.parseDateTime(raw))
      .when(isPlainObject, () => JSON.parse(raw))
      .with(P.number, () => Number(raw))
      .with(P.boolean, () => raw === 'true')
      .otherwise(() => (this.numericFields?.has(key) ? Number(raw) : raw))
  }

  private decodeArrayToken(token: string): number | string {
    return /^-?\d+$/.test(token) ? Number(token) : token
  }

  protected readParam(value: unknown): string | null {
    const first = Array.isArray(value) ? value[0] : value

    return typeof first === 'string' && first.length > 0 ? first : null
  }

  /**
   * A single-value `key[]` param arrives as a plain string, a multi-value one
   * as an array — normalize both to a string array.
   */
  private readArrayParam(value: unknown): string[] | null {
    const items = Array.isArray(value) ? value : [value]
    const tokens = items.filter(
      (item: unknown): item is string => typeof item === 'string' && item.length > 0,
    )

    return tokens.length > 0 ? tokens : null
  }

  private fieldKeys(): string[] {
    return Object.keys(this.getFilteredData())
  }

  /**
   * Get all own enumerable properties of the instance except originalData,
   * appliedData and pagination (the latter owned by BaseFilter, which encodes
   * it separately)
   * @private
   */
  private getFilteredData(): Record<string, unknown> {
    const nonPagination: Record<string, unknown> = {}

    for (const key in this) {
      if (key !== 'originalData' && key !== 'appliedData' && key !== 'pagination') {
        nonPagination[key] = cloneDeep(this[key])
      }
    }

    return nonPagination
  }
}
